/**
 * Scroll-reveal animation system for the landing page.
 *
 * ## Design philosophy
 *
 * Animations follow the page's **narrative arc**, not arbitrary variety.
 * The page is a conversion funnel — each section has a role, and its animation
 * should reinforce that role, not distract from it.
 *
 * ### Two core motions, not five random ones
 *
 * 1. **Rise** — vertical lift. Used for text-narrative sections where the
 *    *message* is the star. The animation stays quiet and lets copy land.
 *
 * 2. **Scale** — zoom into focus. Used for card/collection sections where
 *    individual items should feel like they're "arriving" onto a surface.
 *    The slight spring overshoot gives physical weight.
 *
 * ### Three intensity tiers tied to funnel position
 *
 * - **Gentle** — sections immediately after the hero. The user just arrived;
 *   don't compete with the hero's message. Short duration, small distance.
 *
 * - **Standard** — mid-page narrative. The user is scrolling with intent.
 *   Default duration and distance.
 *
 * - **Emphatic** — key conversion moments (method steps, pricing, CTA).
 *   Longer duration, larger distance, spring easing. These sections
 *   should command attention — the user is deciding.
 *
 * ### Mobile treatment
 *
 * Most users are on mobile. On small screens (< 900px / md breakpoint):
 * - **No blur** — `filter: blur()` is GPU-expensive and causes jank on
 *   mid-range phones. Not worth the visual payoff on a 390px screen.
 * - **No scale** — can cause flicker on mobile Safari, especially combined
 *   with `overflow: hidden`. Scale also looks wrong when content is
 *   already edge-to-edge.
 * - **No spring easing** — overshoot can push content outside the viewport
 *   edge on small screens, looking like a layout bug.
 * - **Smaller distances** — 48px of travel is ~12% of a mobile viewport,
 *   which reads as broken, not elegant. Capped at 16px.
 * - **Shorter durations** — users thumb-scroll faster on mobile, so
 *   animations must resolve before the user scrolls past.
 * - **Tighter stagger** — 60ms instead of 100ms so content is readable
 *   sooner. Card stagger reduced proportionally.
 *
 * The result: mobile gets a clean, fast, reliable `opacity + translateY`
 * treatment that elevates without risking "buggy" perception.
 *
 * ### Stagger
 *
 * Children within each section cascade at 100ms intervals (set via `--stagger`
 * CSS custom property). Card grids get an additional card-level stagger at
 * 130ms intervals with a 180ms base delay, so cards appear to "deal" onto
 * the page after the section header has settled.
 */
import type { SxProps, Theme } from '@mui/material/styles';

// ─── Motion tokens ──────────────────────────────────────────────────────────

const easing = {
  /** Smooth deceleration — the workhorse. */
  smooth: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** Expo-out — snappier attack, long tail. */
  expo: 'cubic-bezier(0.16, 1, 0.3, 1)',
  /** Slight overshoot — physical, used for scale on desktop only. */
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

const duration = {
  gentle: '0.65s',
  standard: '0.8s',
  emphatic: '1s',
} as const;

const distance = {
  gentle: 20,
  standard: 32,
  emphatic: 48,
} as const;

const blur = {
  gentle: 2,
  standard: 3,
  emphatic: 5,
} as const;

const scaleValues = {
  /** For card grids — subtle enough to not distort text. */
  section: 0.94,
  /** Children scale a bit less than the parent. */
  child: 0.97,
} as const;

/** MUI md breakpoint. Below this = mobile treatment. */
const MOBILE_BREAKPOINT = '@media (max-width: 899.95px)';

// ─── Mobile tokens (stripped-down for performance) ──────────────────────────

const mobile = {
  /** Single easing for everything — no spring on mobile. */
  easing: easing.smooth,
  duration: {
    gentle: '0.45s',
    standard: '0.55s',
    emphatic: '0.65s',
  },
  /** Max 16px travel on mobile — larger feels broken on a 390px screen. */
  distance: {
    gentle: 10,
    standard: 14,
    emphatic: 16,
  },
  /** Tighter stagger so content is readable sooner. */
  staggerInterval: '60ms',
  cardStaggerInterval: '80ms',
  cardStaggerBase: '100ms',
} as const;

// ─── Reveal configuration per section ───────────────────────────────────────

/**
 * Motion type: how the section enters.
 * - `rise`: vertical translateY
 * - `scale`: scale + very slight translateY (cards "pop" into place)
 */
type MotionType = 'rise' | 'scale';

/** Intensity tier: how dramatic the entrance is. */
type Intensity = 'gentle' | 'standard' | 'emphatic';

interface RevealConfig {
  motion: MotionType;
  intensity: Intensity;
  /** Why this section gets this treatment. */
  rationale: string;
}

/**
 * Section-to-animation mapping.
 *
 * Each entry documents *why* it uses that motion + intensity, so the system
 * reads as intentional design decisions, not random assignments.
 */
export const sectionRevealMap: Record<string, RevealConfig> = {
  //
  // ── Opening (trust-building) ─────────────────────────────────
  //
  trust: {
    motion: 'rise',
    intensity: 'gentle',
    rationale:
      "First band after hero — quiet entrance that doesn't compete with the hero message.",
  },

  //
  // ── Narrative (building the argument) ────────────────────────
  //
  proof: {
    motion: 'scale',
    intensity: 'standard',
    rationale:
      'The before→after arc and weekly loop "land" on the page like documents placed on a table.',
  },
  'who-teaches': {
    motion: 'rise',
    intensity: 'gentle',
    rationale:
      'Trust-building zone. Credentials should feel grounded, not flashy.',
  },

  //
  // ── Benefits (showing value) ─────────────────────────────────
  //
  audience: {
    motion: 'scale',
    intensity: 'standard',
    rationale:
      'Students & parents cards — same card pattern as the proof band for visual consistency.',
  },

  //
  // ── Conversion (decision point) ──────────────────────────────
  //
  pricing: {
    motion: 'rise',
    intensity: 'emphatic',
    rationale:
      'The money question — emphatic but using rise (not scale) because clarity and directness matter at this decision point.',
  },
  'related-links': {
    motion: 'scale',
    intensity: 'gentle',
    rationale:
      'Exploration links — light and inviting, not demanding. Quick entrance keeps momentum toward CTA.',
  },
  cta: {
    motion: 'rise',
    intensity: 'emphatic',
    rationale:
      'Final ask — confident, deliberate. Rise (not scale) because a CTA should feel grounded and direct, not bouncy.',
  },

  //
  // ── Resolution ───────────────────────────────────────────────
  //
  faq: {
    motion: 'scale',
    intensity: 'gentle',
    rationale:
      'Objection handling — calm, settled. Each FAQ card gets individual attention via stagger.',
  },

  //
  // ── Level & school surfaces ──────────────────────────────────
  // These pages open on a hero, then argue level-by-level. The keys below
  // also catch the namespaced ids those layouts build (`level-p6-facts`)
  // via the suffix lookup in `getRevealAttr`.
  //
  streams: {
    motion: 'scale',
    intensity: 'gentle',
    rationale:
      'Route-choice cards on the hub pages — light and inviting, so picking a level keeps momentum.',
  },
  syllabus: {
    motion: 'rise',
    intensity: 'gentle',
    rationale:
      'First band after the level hero — a quiet entrance so the coverage list reads as reference, not spectacle.',
  },
  challenges: {
    motion: 'scale',
    intensity: 'standard',
    rationale:
      'Pain-point cards — the same card pattern as the homepage proof band, arriving onto the page.',
  },
  'how-we-help': {
    motion: 'rise',
    intensity: 'standard',
    rationale:
      'The answer to the challenges above — narrative, so rise keeps the argument quiet and readable.',
  },
  facts: {
    motion: 'scale',
    intensity: 'standard',
    rationale:
      'Program-fact grid — cards land on the surface, matching the other card collections.',
  },
  fit: {
    motion: 'scale',
    intensity: 'standard',
    rationale:
      'Decision-support proof cards — same treatment as the fact grid they sit beside.',
  },
  related: {
    motion: 'scale',
    intensity: 'gentle',
    rationale:
      "School pages' exploration links. Same treatment as the homepage's `related-links`, under the suffix their namespaced id resolves to.",
  },
  //
  // The school run itself — the cut-off table, the About card, the mid-page
  // trial band, the heritage prose and the school's own FAQs — is deliberately
  // absent, on the same principle as the blog article body below: reference
  // content must be readable the moment it paints. A parent who searched
  // `<school> cut off point` should not wait out an entrance to read the
  // figure, and the ~870 words of heritage prose under it are the same kind of
  // content. `SchoolMathPage` registers only the entries below this note, so
  // adding a school id back to the map animates nothing by itself.
  //
  // It is also what resolves the motion mismatch on that page. Every school
  // section is `Shell > Paper > Stack`, a shape `assignStaggerIndices` cannot
  // reach into, so they entered as whole blocks above a marketing run whose
  // children cascade. The alternative was to widen the stagger selectors here
  // and in that hook — which adds motion to every marketing surface to fix one
  // page. Withholding it instead makes the change of register the boundary
  // between the school's content and the pitch.
  //
  support: {
    motion: 'rise',
    intensity: 'standard',
    rationale:
      'The DeepThink pitch on a school page, and the first band that animates — narrative, so rise keeps the argument quiet and readable.',
  },

  //
  // ── Blog ─────────────────────────────────────────────────────
  // The article body is deliberately absent: prose must be readable the
  // moment it paints. Only the listing's collections and the article's tail
  // are registered.
  //
  featured: {
    motion: 'scale',
    intensity: 'gentle',
    rationale:
      "The listing's lead card — one large item arriving onto the page directly under the hero, so it stays quiet.",
  },
  articles: {
    motion: 'scale',
    intensity: 'standard',
    rationale:
      'The article grid — the same card-collection treatment as the homepage proof band.',
  },

  //
  // ── About & contact ──────────────────────────────────────────
  // These two pages stack full-bleed bands instead of the inset panels every
  // other surface uses, so each entry here is a `rise`: a `scale` on a band
  // that runs edge-to-edge pulls the page background in around all four sides.
  //
  story: {
    motion: 'rise',
    intensity: 'gentle',
    rationale:
      'The origin narrative — long-form prose directly under the hero, so the entrance stays quiet and lets the copy land.',
  },
  mission: {
    motion: 'rise',
    intensity: 'gentle',
    rationale:
      'The social-mission block, a single prose card. Same quiet treatment as the story it continues.',
  },
  system: {
    motion: 'rise',
    intensity: 'standard',
    rationale:
      'The weekly-loop explainer — mid-page, read with intent, so it takes the default rise.',
  },
  teachers: {
    motion: 'rise',
    intensity: 'gentle',
    rationale:
      "The About page's credentials band — the homepage's `who-teaches` argument, so it gets the same grounded treatment.",
  },
  channels: {
    motion: 'rise',
    intensity: 'gentle',
    rationale:
      'Contact channels — a utility band under the form, so it should arrive without asking for attention.',
  },
};

// ─── CSS generation (desktop) ───────────────────────────────────────────────

/** Build the transition shorthand for a given intensity + motion. */
function transitionFor(
  motion: MotionType,
  intensity: Intensity,
  properties: string[] = ['opacity', 'transform', 'filter']
): string {
  const dur = duration[intensity];
  const ease = motion === 'scale' ? easing.spring : easing.smooth;
  if (motion === 'scale') {
    return properties
      .map(p => `${p} ${dur} ${p === 'opacity' ? easing.expo : ease}`)
      .join(', ');
  }
  return properties.map(p => `${p} ${dur} ${ease}`).join(', ');
}

/** Build hidden + visible styles for a given reveal config (desktop). */
function buildRevealPair(
  motion: MotionType,
  intensity: Intensity
): { hidden: Record<string, unknown>; visible: Record<string, unknown> } {
  if (motion === 'rise') {
    return {
      hidden: {
        opacity: 0,
        transform: `translateY(${distance[intensity]}px)`,
        filter: `blur(${blur[intensity]}px)`,
        willChange: 'opacity, transform, filter',
      },
      visible: {
        opacity: 1,
        transform: 'translateY(0)',
        filter: 'blur(0)',
        transition: transitionFor(motion, intensity),
        willChange: 'auto',
      },
    };
  }
  return {
    hidden: {
      opacity: 0,
      transform: `scale(${scaleValues.section}) translateY(${Math.round(distance[intensity] * 0.35)}px)`,
      filter: `blur(${blur[intensity]}px)`,
      willChange: 'opacity, transform, filter',
    },
    visible: {
      opacity: 1,
      transform: 'scale(1) translateY(0)',
      filter: 'blur(0)',
      transition: transitionFor(motion, intensity),
      willChange: 'auto',
    },
  };
}

/** Build hidden + visible styles for children (desktop). */
function buildChildPair(
  motion: MotionType,
  intensity: Intensity
): { hidden: Record<string, unknown>; visible: Record<string, unknown> } {
  const childDist = Math.round(distance[intensity] * 0.6);
  const childBlur = Math.max(1, blur[intensity] - 1);

  if (motion === 'rise') {
    return {
      hidden: {
        opacity: 0,
        transform: `translateY(${childDist}px)`,
        filter: `blur(${childBlur}px)`,
      },
      visible: {
        opacity: 1,
        transform: 'translateY(0)',
        filter: 'blur(0)',
        transition: transitionFor(motion, intensity),
        transitionDelay: 'calc(var(--stagger, 0) * 100ms)',
      },
    };
  }
  return {
    hidden: {
      opacity: 0,
      transform: `scale(${scaleValues.child}) translateY(${Math.round(childDist * 0.5)}px)`,
      filter: `blur(${childBlur}px)`,
    },
    visible: {
      opacity: 1,
      transform: 'scale(1) translateY(0)',
      filter: 'blur(0)',
      transition: transitionFor(motion, intensity),
      transitionDelay: 'calc(var(--stagger, 0) * 100ms)',
    },
  };
}

/** Generate desktop CSS rules for a given data-reveal value. */
function revealRules(
  name: string,
  motion: MotionType,
  intensity: Intensity
): Record<string, unknown> {
  const section = buildRevealPair(motion, intensity);
  const child = buildChildPair(motion, intensity);
  const sel = (suffix: string) =>
    `& .section-fade[data-reveal="${name}"]${suffix}`;
  const childWrappers = ['.MuiContainer-root', '.MuiStack-root'];

  const rules: Record<string, unknown> = {
    [sel('')]: section.hidden,
    [sel('.section-visible')]: section.visible,
  };

  const childHiddenSel = childWrappers
    .map(w => `${sel('')} > ${w} > *`)
    .join(', ');
  rules[childHiddenSel] = child.hidden;

  const childVisibleSel = childWrappers
    .map(w => `${sel('.section-visible')} > ${w} > *`)
    .join(', ');
  rules[childVisibleSel] = child.visible;

  return rules;
}

// ─── CSS generation (mobile overrides) ──────────────────────────────────────

/**
 * Build mobile override rules for a data-reveal value.
 *
 * On mobile, every variant collapses to: opacity + small translateY.
 * No blur. No scale. No spring. Just clean, fast, reliable.
 * The intensity still affects distance and duration proportionally,
 * but within the tighter mobile range.
 */
function mobileRevealRules(
  name: string,
  intensity: Intensity
): Record<string, unknown> {
  const dist = mobile.distance[intensity];
  const childDist = Math.round(dist * 0.6);
  const dur = mobile.duration[intensity];
  const ease = mobile.easing;

  const sel = (suffix: string) =>
    `& .section-fade[data-reveal="${name}"]${suffix}`;
  const childWrappers = ['.MuiContainer-root', '.MuiStack-root'];

  const transition = `opacity ${dur} ${ease}, transform ${dur} ${ease}`;

  const rules: Record<string, unknown> = {
    [sel('')]: {
      opacity: 0,
      transform: `translateY(${dist}px)`,
      filter: 'none',
      willChange: 'opacity, transform',
    },
    [sel('.section-visible')]: {
      opacity: 1,
      transform: 'translateY(0)',
      filter: 'none',
      transition,
      willChange: 'auto',
    },
  };

  const childHiddenSel = childWrappers
    .map(w => `${sel('')} > ${w} > *`)
    .join(', ');
  rules[childHiddenSel] = {
    opacity: 0,
    transform: `translateY(${childDist}px)`,
    filter: 'none',
  };

  const childVisibleSel = childWrappers
    .map(w => `${sel('.section-visible')} > ${w} > *`)
    .join(', ');
  rules[childVisibleSel] = {
    opacity: 1,
    transform: 'translateY(0)',
    filter: 'none',
    transition,
    transitionDelay: `calc(var(--stagger, 0) * ${mobile.staggerInterval})`,
  };

  return rules;
}

// ─── Collect all reveal variant names ───────────────────────────────────────

/** All unique data-reveal values generated by the system. */
interface RevealVariant {
  name: string;
  motion: MotionType;
  intensity: Intensity;
}

const revealVariants: RevealVariant[] = [
  { name: 'rise-gentle', motion: 'rise', intensity: 'gentle' },
  { name: 'rise-standard', motion: 'rise', intensity: 'standard' },
  { name: 'rise-emphatic', motion: 'rise', intensity: 'emphatic' },
  { name: 'scale-gentle', motion: 'scale', intensity: 'gentle' },
  { name: 'scale-standard', motion: 'scale', intensity: 'standard' },
];

// ─── Exported styles ────────────────────────────────────────────────────────

/**
 * Complete scroll-reveal sx styles. Spread into the landing page's root `sx`.
 *
 * Usage: `<Box sx={{ ...landingRevealStyles, /* other styles * / }} />`
 */
export const landingRevealStyles: SxProps<Theme> = {
  // ── Desktop reveal rules (all variants) ──
  ...revealVariants.reduce<Record<string, unknown>>(
    (acc, v) => ({ ...acc, ...revealRules(v.name, v.motion, v.intensity) }),
    {}
  ),

  // ── Desktop card-level stagger ──
  '& .section-fade .MuiPaper-root[style*="--card-stagger"]': {
    opacity: 0,
    transform: `translateY(16px) scale(${scaleValues.child})`,
  },
  '& .section-fade.section-visible .MuiPaper-root[style*="--card-stagger"]': {
    opacity: 1,
    transform: 'translateY(0) scale(1)',
    transition: `opacity 0.7s ${easing.expo}, transform 0.7s ${easing.spring}`,
    transitionDelay: 'calc(var(--card-stagger, 0) * 130ms + 180ms)',
  },

  // ── Mobile overrides ──
  // Strip blur, scale, spring. Opacity + small translateY only.
  [MOBILE_BREAKPOINT]: {
    // Override each variant with mobile-safe values
    ...revealVariants.reduce<Record<string, unknown>>(
      (acc, v) => ({ ...acc, ...mobileRevealRules(v.name, v.intensity) }),
      {}
    ),

    // Mobile card stagger: no scale, tighter timing
    '& .section-fade .MuiPaper-root[style*="--card-stagger"]': {
      opacity: 0,
      transform: 'translateY(12px)',
    },
    '& .section-fade.section-visible .MuiPaper-root[style*="--card-stagger"]': {
      opacity: 1,
      transform: 'translateY(0)',
      transition: `opacity 0.5s ${mobile.easing}, transform 0.5s ${mobile.easing}`,
      transitionDelay: `calc(var(--card-stagger, 0) * ${mobile.cardStaggerInterval} + ${mobile.cardStaggerBase})`,
    },
  },

  // ── Instant reveal (anchor targets) ──
  //
  // A section reached by a hash is already under the reader's eyes when it
  // reveals, so playing the entrance slides the content out from under them:
  // the browser computes the scroll position while the section still carries
  // its hidden `translateY`, then the transition walks it ~20px to its resting
  // place *after* they land. `section-instant` cancels the transition so the
  // section is simply already there. Written with the `[data-reveal]` attribute
  // so it outranks the per-variant `.section-visible` rules above on
  // specificity rather than on source order.
  '& .section-fade[data-reveal].section-instant.section-visible, & .section-fade[data-reveal].section-instant.section-visible > .MuiContainer-root > *, & .section-fade[data-reveal].section-instant.section-visible > .MuiStack-root > *, & .section-fade[data-reveal].section-instant.section-visible .MuiPaper-root[style*="--card-stagger"]':
    {
      transition: 'none',
      transitionDelay: '0s',
    },

  // ── Reduced motion: disable everything ──
  '@media (prefers-reduced-motion: reduce)': {
    '& .section-fade, & .section-fade[data-reveal]': {
      opacity: 1,
      transform: 'none',
      filter: 'none',
      transition: 'none',
    },
    '& .section-fade > .MuiContainer-root > *, & .section-fade > .MuiStack-root > *, & .section-fade .MuiPaper-root[style*="--card-stagger"]':
      {
        opacity: 1,
        transform: 'none',
        filter: 'none',
        transition: 'none',
      },
  },
} as SxProps<Theme>;

/**
 * Resolve a section ID to its `data-reveal` attribute value.
 * Returns a string like `"rise-gentle"` or `"scale-standard"`.
 *
 * Level and school layouts namespace their section ids per page
 * (`level-p6-faq`, `school-acsi-facts`) so anchors stay unique, so an id with
 * no exact entry is retried on its last segment. Anything still unmapped
 * falls back to `"rise-standard"`.
 */
export function getRevealAttr(sectionId: string): string {
  const lastSegment = sectionId.slice(sectionId.lastIndexOf('-') + 1);
  const config = sectionRevealMap[sectionId] ?? sectionRevealMap[lastSegment];
  if (!config) return 'rise-standard';
  return `${config.motion}-${config.intensity}`;
}
